import { transactionApi, budgetApi, getAccessToken } from '@/lib/api';
import { transactionDb, budgetDb, db } from '@/lib/db';
import { queueOfflineChange, shouldUseLocalData } from '@/lib/offlineQueue';
import type {
  Transaction,
  TransactionSummary,
  CreateTransactionRequest,
  UpdateTransactionRequest,
} from '@/types';

function summaryToTransaction(summary: TransactionSummary): Transaction {
  return {
    id: summary.id,
    budgetId: summary.budgetId,
    budgetName: summary.budgetName,
    amount: summary.amount,
    currency: summary.currency,
    date: summary.date,
    merchant: summary.merchant,
    category: summary.category,
    convertedAmount: summary.convertedAmount ?? null,
    note: null,
  };
}

async function fetchAllBudgetTreeTransactions(
  budgetId: string,
  includeDescendants: boolean
): Promise<Transaction[]> {
  const pageSize = 100;
  let page = 0;
  const all: TransactionSummary[] = [];

  while (true) {
    const response = await budgetApi.getTransactions(budgetId, {
      includeDescendants,
      page,
      size: pageSize,
    });
    all.push(...response.content);
    if (page >= response.totalPages - 1) {
      break;
    }
    page += 1;
  }

  return all.map(summaryToTransaction);
}

async function collectDescendantBudgetIds(rootId: string): Promise<string[]> {
  const budgets = await db.budgets.toArray();
  const ids = new Set<string>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const b of budgets) {
      if (b.parentId && ids.has(b.parentId) && !ids.has(b.id)) {
        ids.add(b.id);
        changed = true;
      }
    }
  }
  return Array.from(ids);
}

/**
 * Transaction domain service.
 * Online: backend API. Offline / localUnlocked: Dexie + sync queue.
 */
export const TransactionService = {
  /**
   * Get transactions for a budget from the backend (or Dexie when offline).
   */
  async getByBudget(budgetId: string): Promise<Transaction[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      return transactionDb.getByBudget(budgetId);
    }
    return transactionApi.getByBudget(budgetId);
  },

  /**
   * Get transactions for a budget tree (optionally including descendants).
   */
  async getByBudgetTree(
    budgetId: string,
    includeDescendants: boolean
  ): Promise<Transaction[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      if (!includeDescendants) {
        return transactionDb.getByBudget(budgetId);
      }
      const ids = await collectDescendantBudgetIds(budgetId);
      const chunks = await Promise.all(ids.map((id) => transactionDb.getByBudget(id)));
      return chunks.flat().sort((a, b) => b.date.localeCompare(a.date));
    }
    return fetchAllBudgetTreeTransactions(budgetId, includeDescendants);
  },

  /**
   * Get transaction by ID from the backend (or Dexie when offline).
   */
  async getById(id: string): Promise<Transaction | null> {
    if (shouldUseLocalData(!!getAccessToken())) {
      return (await transactionDb.getById(id)) ?? null;
    }
    try {
      const transaction = await transactionApi.getById(id);
      return transaction;
    } catch (_error) {
      return null;
    }
  },

  /**
   * Get transactions by date range from the backend (or Dexie when offline).
   */
  async getByDateRange(
    budgetId: string, 
    startDate: string, 
    endDate: string
  ): Promise<Transaction[]> {
    if (shouldUseLocalData(!!getAccessToken())) {
      return transactionDb.getByDateRange(budgetId, startDate, endDate);
    }
    return transactionApi.getByDateRange(budgetId, startDate, endDate);
  },

  /**
   * Create a new transaction — API online, Dexie+queue offline.
   */
  async create(request: CreateTransactionRequest): Promise<Transaction> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const budget = await budgetDb.getById(request.budgetId);
      const now = new Date().toISOString();
      const transaction: Transaction = {
        id: crypto.randomUUID(),
        budgetId: request.budgetId,
        budgetName: budget?.name,
        amount: request.amount,
        currency: request.currency,
        date: request.date,
        note: request.note ?? null,
        merchant: request.merchant ?? null,
        category: request.category ?? null,
        version: 0,
        lastModified: now,
        createdAt: now,
      };
      await transactionDb.save(transaction);
      await queueOfflineChange({
        entityType: 'TRANSACTION',
        entityId: transaction.id,
        changeType: 'CREATE',
        data: JSON.stringify(transaction),
        version: transaction.version ?? 0,
      });
      return transaction;
    }
    return transactionApi.create(request);
  },

  /**
   * Update an existing transaction — API online, Dexie+queue offline.
   */
  async update(id: string, request: UpdateTransactionRequest): Promise<Transaction> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const existing = await transactionDb.getById(id);
      if (!existing) throw new Error('Transaction not found offline');
      const updated: Transaction = {
        ...existing,
        budgetId: request.budgetId ?? existing.budgetId,
        amount: request.amount,
        currency: request.currency,
        date: request.date,
        note: request.note ?? existing.note,
        merchant: request.merchant ?? existing.merchant,
        category: request.category ?? existing.category,
        version: (existing.version ?? request.version) + 1,
        lastModified: new Date().toISOString(),
      };
      await transactionDb.save(updated);
      await queueOfflineChange({
        entityType: 'TRANSACTION',
        entityId: id,
        changeType: 'UPDATE',
        data: JSON.stringify(updated),
        version: updated.version ?? 0,
      });
      return updated;
    }
    return transactionApi.update(id, request);
  },

  /**
   * Delete a transaction — API online, Dexie+queue offline.
   */
  async delete(id: string): Promise<void> {
    if (shouldUseLocalData(!!getAccessToken())) {
      const existing = await transactionDb.getById(id);
      await transactionDb.delete(id);
      await queueOfflineChange({
        entityType: 'TRANSACTION',
        entityId: id,
        changeType: 'DELETE',
        data: JSON.stringify(existing ?? { id }),
        version: existing?.version ?? 0,
      });
      return;
    }
    await transactionApi.delete(id);
  },

  /**
   * Check if editing this transaction should show a warning.
   */
  isHistorical(transaction: Transaction): boolean {
    return new Date(transaction.date) < new Date(new Date().toDateString());
  },

  /**
   * Calculate sum of transactions.
   */
  sumTransactions(transactions: Transaction[]): number {
    return transactions.reduce((sum, t) => {
      return sum + (t.convertedAmount ?? t.amount);
    }, 0);
  },

  /**
   * Group transactions by date.
   */
  groupByDate(transactions: Transaction[]): Map<string, Transaction[]> {
    const groups = new Map<string, Transaction[]>();
    
    for (const transaction of transactions) {
      const date = transaction.date;
      const existing = groups.get(date) ?? [];
      existing.push(transaction);
      groups.set(date, existing);
    }
    
    return groups;
  },

  /**
   * Group transactions by category.
   */
  groupByCategory(transactions: Transaction[]): Map<string, Transaction[]> {
    const groups = new Map<string, Transaction[]>();
    
    for (const transaction of transactions) {
      const category = transaction.category ?? 'Uncategorized';
      const existing = groups.get(category) ?? [];
      existing.push(transaction);
      groups.set(category, existing);
    }
    
    return groups;
  },

  /**
   * Validate transaction data.
   */
  validate(data: Partial<CreateTransactionRequest>): string[] {
    const errors: string[] = [];

    if (!data.budgetId) {
      errors.push('Budget is required');
    }

    if (data.amount === undefined) {
      errors.push('Amount is required');
    }

    if (!data.currency || data.currency.length !== 3) {
      errors.push('Valid currency code is required');
    }

    if (!data.date) {
      errors.push('Date is required');
    }

    if (data.note && data.note.length > 500) {
      errors.push('Note must be 500 characters or less');
    }

    if (data.merchant && data.merchant.length > 100) {
      errors.push('Merchant must be 100 characters or less');
    }

    if (data.category && data.category.length > 50) {
      errors.push('Category must be 50 characters or less');
    }

    return errors;
  },
};
